import React, { useMemo } from "react";
import snakeCase from "lodash/snakeCase";
import useAuth from "../../hooks/useAuth";
import { useCustomProjectFields } from "../../contexts/clients";
import { useConfigurableFields } from "../../contexts/configurable-fields";
import useEnabledCustomFieldIds from "../capture/useEnabledCustomFieldIds";
import CustomSelectField from "../../components/TablePro/fields/CustomSelectField";
import CustomSelectFieldWithOptions from "../../components/TablePro/fields/CustomSelectFieldWithOptions";
import CustomFieldsBooleanDisplayField from "../../components/TablePro/fields/CustomFieldsBooleanDisplayField";
import { getFieldSettingValue } from "../../utils/fieldSettings";

const OPTIONS = [
  ["true", "Yes"],
  ["false", "No"],
  ["", "n/a"],
];

const getCustomField = field => {
  const baseField = {
    id: `customField${field.id}`,
    label: field.defaultLabel,
    visible: true,
  };

  if (field.availableOptions && field.availableOptions.length > 0) {
    return {
      ...baseField,
      input: props => (
        <CustomSelectFieldWithOptions
          availableOptions={field.availableOptions}
          restrictToAvailableOptions={field.restrictToAvailableOptions}
          {...props}
        />
      ),
    };
  }

  if (field.fieldType === "boolean") {
    return {
      ...baseField,
      input: props => <CustomSelectField {...props} options={OPTIONS} />,
      component: CustomFieldsBooleanDisplayField,
    };
  }

  return baseField;
};

export default function useProjectSpecificFields(fields, options = {}) {
  const { user } = useAuth();
  const { customFields } = useConfigurableFields();
  const displayProjectFields = useCustomProjectFields();
  const fieldPresence = options.assetFieldPresence;
  const presenceCustomFieldIds = fieldPresence?.customFieldTemplateIds || [];
  const enabledCustomFields = useEnabledCustomFieldIds(customFields, displayProjectFields || {});
  const enabledCustomFieldIdSet = new Set(enabledCustomFields.map(field => field.id));
  const presenceCustomFields = customFields.filter(
    field => presenceCustomFieldIds.includes(field.id) && !enabledCustomFieldIdSet.has(field.id)
  );
  const visibleCustomFields = [...enabledCustomFields, ...presenceCustomFields];
  const displayCustomFields = Object.fromEntries(
    visibleCustomFields.map(field => [`custom_field_${field.id}`, true])
  );

  const mappedFields = fields.map(f => {
    if (f.id.startsWith("customField")) {
      const customFieldId = f.id.replace("customField", "");
      const customField = visibleCustomFields.find(cf => cf.id === customFieldId);
      return customField
        ? {
            ...f,
            ...getCustomField(customField),
          }
        : f;
    } else {
      return f;
    }
  });

  const existingCustomFieldIdSet = new Set(
    mappedFields
      .filter(f => f.id.startsWith("customField"))
      .map(f => f.id.replace("customField", ""))
  );
  const missingCustomFields = visibleCustomFields
    .filter(cf => !existingCustomFieldIdSet.has(cf.id))
    .map(cf => getCustomField(cf));

  const allFields = [...mappedFields, ...missingCustomFields];

  return useMemo(() => {
    if (!user.company.settings.project_specific_fields || !displayProjectFields) {
      return allFields;
    }

    const projectFields = {
      ...displayProjectFields,
      barcode: getFieldSettingValue(displayProjectFields, "barcode_number"),
      photo_upload: getFieldSettingValue(displayProjectFields, "photo"),
      photo_carousel: getFieldSettingValue(displayProjectFields, "photo"),
      ...displayCustomFields,
    };

    return allFields.map(field => ({
      ...field,
      visible: projectFields[snakeCase(field.id)],
    }));
  }, [allFields, displayProjectFields, user.company.settings.project_specific_fields]);
}
